// src/components/operationCenter/widgets/TokenMetricsCardWidget.tsx
// Mini dashboard card for a single token: header, sparkline, key stats

import React, { useEffect, useRef, useMemo } from "react";
import * as echarts from "echarts";
import type { ECharts, EChartsOption } from "echarts";
import { TrendingUp, TrendingDown } from "lucide-react";
import { Link } from "react-router-dom";
import type { WidgetProps, TokenMetricsCardConfig } from "@/types/dashboard";
import { useTokensScreenerData } from "@/hooks/tokens/useTokensScreenerData";
import { useTokensChanges, useTokensHistory } from "@/hooks/tokens/compare/useCompareData";
import { MetricKeys } from "@/constants/metrics";

const TokenMetricsCardWidget: React.FC<WidgetProps<TokenMetricsCardConfig>> = ({ widget }) => {
  const config = widget.config as TokenMetricsCardConfig;
  const ticker = config.ticker?.toUpperCase() ?? "BTC";

  const chartRef = useRef<HTMLDivElement>(null);
  const chartInstanceRef = useRef<ECharts | null>(null);

  // Fetch token data
  const { data: rows, isLoading: screenerLoading } = useTokensScreenerData();
  const { changeByTicker, query: changesQuery } = useTokensChanges();
  const { data: historyData, isLoading: historyLoading } = useTokensHistory([ticker], MetricKeys.PriceClose, true);

  const isLoading = screenerLoading || changesQuery.isLoading || historyLoading;

  // Find token row
  const tokenRow = useMemo(() => {
    if (!Array.isArray(rows)) return null;
    return rows.find((r) => String(r["tokenSymbol"] ?? "").toUpperCase() === ticker);
  }, [rows, ticker]);

  // Get changes
  const changes = changeByTicker.get(ticker);

  // Extract values - Core metrics
  const price = tokenRow?.[MetricKeys.PriceClose] as number | null;
  const marketCap = tokenRow?.[MetricKeys.MarketCap] as number | null;
  const volume24h = tokenRow?.[MetricKeys.Volume] as number | null;
  const logoUrl = tokenRow?.["logoUrl"] as string | undefined;
  const tokenName = tokenRow?.["tokenName"] as string | undefined;

  // Fundamentals
  const fees = tokenRow?.[MetricKeys.Fees] as number | null;
  const revenue = tokenRow?.[MetricKeys.Revenue] as number | null;

  // Ratios
  const mcToFees = tokenRow?.[MetricKeys.Ratio_MarketCapToAnnualisedFees] as number | null;
  const mcToTvl = tokenRow?.[MetricKeys.Ratio_MarketCapToTvl] as number | null;
  const feesToTx = tokenRow?.[MetricKeys.Ratio_AnnualisedFeesToTransactions] as number | null;

  // Derivatives
  const openInterest = tokenRow?.[MetricKeys.FuturesOI] as number | null;
  const fundingRate = tokenRow?.[MetricKeys.FuturesFROIW] as number | null;

  // Price changes
  const change1d = changes?.[`${MetricKeys.PriceClose}_1d`] as number | null;
  const change7d = changes?.[`${MetricKeys.PriceClose}_7d`] as number | null;
  const change30d = changes?.[`${MetricKeys.PriceClose}_30d`] as number | null;

  // Fees/Revenue changes
  const feesChange1d = changes?.[`${MetricKeys.Fees}_1d`] as number | null;
  const revenueChange1d = changes?.[`${MetricKeys.Revenue}_1d`] as number | null;

  // Process history for sparkline
  const sparklineData = useMemo(() => {
    if (!historyData || !Array.isArray(historyData)) return { dates: [], values: [] };

    const tokenData = historyData.find(
      (t) => t.ticker?.toUpperCase() === ticker
    );
    if (!tokenData?.metrics?.[0]) return { dates: [], values: [] };

    const metric = tokenData.metrics[0];
    const dates = metric.dates ?? [];
    const values = metric.values ?? [];

    // Get last 30 days
    const last30Dates = dates.slice(-30);
    const last30Values = values.slice(-30);

    return {
      dates: last30Dates,
      values: last30Values,
    };
  }, [historyData, ticker]);

  // Sparkline chart
  useEffect(() => {
    const container = chartRef.current;
    if (!container || sparklineData.values.length === 0 || isLoading) return;

    if (chartInstanceRef.current) {
      chartInstanceRef.current.dispose();
      chartInstanceRef.current = null;
    }

    const initTimer = setTimeout(() => {
      const chart = echarts.init(container);
      chartInstanceRef.current = chart;

      const isPositive = (change7d ?? 0) >= 0;
      const color = isPositive ? "#10b981" : "#ef4444";

      const option: EChartsOption = {
        backgroundColor: "transparent",
        grid: { top: 4, right: 4, bottom: 4, left: 4 },
        xAxis: { type: "category", data: sparklineData.dates, show: false },
        yAxis: { type: "value", show: false },
        series: [
          {
            type: "line",
            data: sparklineData.values,
            smooth: true,
            symbol: "none",
            lineStyle: { color, width: 1.5 },
            areaStyle: {
              color: {
                type: "linear",
                x: 0,
                y: 0,
                x2: 0,
                y2: 1,
                colorStops: [
                  { offset: 0, color: isPositive ? "rgba(16, 185, 129, 0.2)" : "rgba(239, 68, 68, 0.2)" },
                  { offset: 1, color: isPositive ? "rgba(16, 185, 129, 0.02)" : "rgba(239, 68, 68, 0.02)" },
                ],
              },
            },
          },
        ],
      };

      chart.setOption(option);
      chart.resize();
    }, 50);

    const handleResize = () => chartInstanceRef.current?.resize();
    const resizeObserver = new ResizeObserver(handleResize);
    resizeObserver.observe(container);
    window.addEventListener("resize", handleResize);

    return () => {
      clearTimeout(initTimer);
      window.removeEventListener("resize", handleResize);
      resizeObserver.disconnect();
    };
  }, [sparklineData, change7d, isLoading]);

  useEffect(() => {
    return () => {
      chartInstanceRef.current?.dispose();
    };
  }, []);

  // Formatters
  const formatPrice = (val: number | null) => {
    if (val === null) return "—";
    if (val >= 1000) return `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
    if (val >= 1) return `$${val.toFixed(2)}`;
    return `$${val.toPrecision(4)}`;
  };

  const formatFull = (val: number | null) => {
    if (val === null) return "—";
    return `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
  };

  const formatCompact = (val: number | null, prefix = "$") => {
    if (val === null) return "—";
    if (val >= 1e12) return `${prefix}${(val / 1e12).toFixed(1)}T`;
    if (val >= 1e9) return `${prefix}${(val / 1e9).toFixed(1)}B`;
    if (val >= 1e6) return `${prefix}${(val / 1e6).toFixed(1)}M`;
    if (val >= 1e3) return `${prefix}${(val / 1e3).toFixed(1)}K`;
    return `${prefix}${val.toFixed(0)}`;
  };

  const formatChange = (val: number | null) => {
    if (val === null) return "—";
    return `${val >= 0 ? "+" : ""}${val.toFixed(1)}%`;
  };

  const formatFundingRate = (val: number | null) => {
    if (val === null) return "—";
    // Funding rate is typically in decimal form, convert to percentage
    const pct = val * 100;
    return `${pct >= 0 ? "+" : ""}${pct.toFixed(4)}%`;
  };

  const formatChangeInline = (val: number | null) => {
    if (val === null) return "";
    return ` (${val >= 0 ? "+" : ""}${val.toFixed(0)}%)`;
  };

  const formatRatio = (val: number | null) => {
    if (val === null) return "—";
    // No K/M/B - just raw number with 1dp, min 2 significant figures
    if (val >= 100) return val.toFixed(0);
    if (val >= 10) return val.toFixed(1);
    return val.toFixed(2);
  };

  if (isLoading) {
    return (
      <div className="h-full flex flex-col p-3">
        <div className="animate-pulse space-y-2">
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 bg-slate-700/50 rounded-full" />
            <div className="h-4 w-16 bg-slate-700/50 rounded" />
          </div>
          <div className="h-12 bg-slate-700/30 rounded" />
          <div className="grid grid-cols-3 gap-2">
            <div className="h-8 bg-slate-700/30 rounded" />
            <div className="h-8 bg-slate-700/30 rounded" />
            <div className="h-8 bg-slate-700/30 rounded" />
          </div>
        </div>
      </div>
    );
  }

  const isUp = (change1d ?? 0) >= 0;

  return (
    <div className="h-full flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex items-center justify-between px-3 py-2 border-b border-slate-700/30 flex-shrink-0">
        <Link
          to={`/app/tokens/${ticker.toLowerCase()}`}
          className="flex items-center gap-2 hover:opacity-80 transition-opacity"
        >
          {logoUrl && <img src={logoUrl} alt="" className="w-6 h-6 rounded-full" />}
          <div>
            <div className="text-sm font-semibold text-white">{ticker}</div>
            {tokenName && (
              <div className="text-[10px] text-slate-500 truncate max-w-[100px]">{tokenName}</div>
            )}
          </div>
        </Link>
        <div className="text-right">
          <div className="text-sm font-bold text-white">{formatPrice(price)}</div>
          <div className={`flex items-center justify-end gap-0.5 text-[10px] font-medium ${isUp ? "text-emerald-400" : "text-red-400"}`}>
            {isUp ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
            {formatChange(change1d)}
          </div>
        </div>
      </div>

      {/* Sparkline + Price Changes */}
      <div className="flex-shrink-0">
        <div ref={chartRef} style={{ height: 45, width: "100%" }} />
        {/* Price Changes Row - under sparkline */}
        <div className="flex items-center justify-around px-3 pb-2">
          <div className="text-center">
            <span className="text-[9px] text-slate-500">24h </span>
            <span className={`text-[10px] font-semibold ${(change1d ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatChange(change1d)}
            </span>
          </div>
          <div className="text-center">
            <span className="text-[9px] text-slate-500">7d </span>
            <span className={`text-[10px] font-semibold ${(change7d ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatChange(change7d)}
            </span>
          </div>
          <div className="text-center">
            <span className="text-[9px] text-slate-500">30d </span>
            <span className={`text-[10px] font-semibold ${(change30d ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatChange(change30d)}
            </span>
          </div>
        </div>
      </div>

      {/* Stats - Compact rows */}
      <div className="flex-1 px-3 py-1 min-h-0 overflow-y-auto space-y-1 border-t border-slate-700/30">
        {/* Market Cap - Full representation */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500">Market Cap</span>
          <span className="text-[11px] font-medium text-slate-200">{formatFull(marketCap)}</span>
        </div>

        {/* 24h Volume */}
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-500">24h Volume</span>
          <span className="text-[11px] font-medium text-slate-200">{formatCompact(volume24h)}</span>
        </div>

        {/* Open Interest */}
        {openInterest !== null && (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-500">Open Interest</span>
            <span className="text-[11px] font-medium text-slate-200">{formatCompact(openInterest)}</span>
          </div>
        )}

        {/* Funding Rate */}
        {fundingRate !== null && (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-500">Funding Rate</span>
            <span className={`text-[11px] font-medium ${(fundingRate ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {formatFundingRate(fundingRate)}
            </span>
          </div>
        )}

        {/* Fees (with 24h change) */}
        {fees !== null && fees > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-500">Fees (24h)</span>
            <span className="text-[11px] font-medium text-slate-200">
              {formatCompact(fees)}
              <span className={`text-[9px] ${(feesChange1d ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatChangeInline(feesChange1d)}
              </span>
            </span>
          </div>
        )}

        {/* Revenue (with 24h change) */}
        {revenue !== null && revenue > 0 && (
          <div className="flex items-center justify-between">
            <span className="text-[10px] text-slate-500">Revenue (24h)</span>
            <span className="text-[11px] font-medium text-slate-200">
              {formatCompact(revenue)}
              <span className={`text-[9px] ${(revenueChange1d ?? 0) >= 0 ? "text-emerald-400" : "text-red-400"}`}>
                {formatChangeInline(revenueChange1d)}
              </span>
            </span>
          </div>
        )}

        {/* Ratios Section */}
        {(mcToFees !== null || mcToTvl !== null || feesToTx !== null) && (
          <>
            <div className="border-t border-slate-700/20 my-1" />
            {mcToFees !== null && (
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">MC / Fees</span>
                <span className="text-[11px] font-medium text-slate-200">{formatRatio(mcToFees)}x</span>
              </div>
            )}
            {mcToTvl !== null && (
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">MC / TVL</span>
                <span className="text-[11px] font-medium text-slate-200">{formatRatio(mcToTvl)}x</span>
              </div>
            )}
            {feesToTx !== null && (
              <div className="flex items-center justify-between">
                <span className="text-[10px] text-slate-500">Fees / Txn</span>
                <span className="text-[11px] font-medium text-slate-200">{formatPrice(feesToTx)}</span>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default TokenMetricsCardWidget;
